import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function ChemistryOfProgress() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Architecture &middot; Evidence &middot; Honesty</span>
          <h1>The Chemistry of Progress</h1>
          <p className="calculus-hero-sub">Progress is the delta between what the model could do yesterday and what it can do today. Measured in accuracy, cost, and architectural honesty.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* Definition */}
          <section className="calc-section reveal">
            <h2>What Progress Means Here</h2>
            <p className="calc-lead">
              Progress is not a marketing word. In this project, progress has a specific,
              measurable definition.
            </p>
            <p>
              Progress is the delta between what the model could do yesterday and what
              it can do today, measured in accuracy, cost, and architectural honesty.
            </p>
            <p>
              We do not cherry-pick outputs. We do not post-process answers to look
              smarter than they are. What the model produces is what you see. It will
              be wrong sometimes. That is honesty. That is also how you know it is real.
            </p>
          </section>

          <Pulse />

          {/* Day 1 */}
          <section className="calc-section reveal">
            <h2>Day 1: The Brain Knew. The Mouth Was Broken.</h2>
            <p>
              March 29, 2026. A 14.2 million parameter modular brain with
              256-dimensional hidden states. Character-level encoding, where
              every letter is its own token. It trained for 2,000 epochs on
              the T1000 GPU. Overnight. While its creator slept.
            </p>
            <p>
              95.5% accuracy on the training set. The model <em>knew</em> the
              answers. It knew that <em>a</em> is a vowel, phonogram card 22,
              with three sounds. It knew that <em>ch</em> makes three sounds.
              It knew the FLOSS rule, the five jobs of silent e, and the six
              sounds of <em>ough</em>.
            </p>
            <p>
              But when you asked it "What is a?", it said:
            </p>
            <blockquote className="calc-quote">
              W is a olea.eItstsavhonogramecahd l2 in the teaching hrder. It has 3 sounds.
            </blockquote>
            <p>
              The knowledge was there. Card 22. Three sounds. Teaching order. All correct,
              buried under garbled characters. A brain that understood phonics but could
              not speak clearly, because we gave it a character-level mouth for a
              word-level mind.
            </p>
          </section>

          <Pulse />

          {/* Day 2 */}
          <section className="calc-section reveal">
            <h2>Day 2: We Fixed the Mouth.</h2>
            <p>
              March 30. We built a custom BPE tokenizer trained on our domain data.
              8,192 vocabulary tokens. "Phonogram" became a single token instead of
              nine characters. "Vowel" became one token. "CVC pattern" became three.
            </p>
            <p>
              We doubled the hidden dimension to 512. Extended sequence length to 256.
              22 million parameters. Loaded the same 954 training pairs. Launched an
              NVIDIA H200 on Vast.ai.
            </p>
            <p>
              15 minutes. $0.52. 97.6% accuracy.
            </p>
            <p>
              Now when you ask it "What is a?", it says:
            </p>
            <blockquote className="calc-quote">
              A is a vowel. It is phonogram card 22 in the teaching order. It has
              3 sounds. The first sound is short a as in at. The second sound is
              long a as in navy, which happens at the end of a syllable according
              to Rule 4. The third sound is ah as in want. When a says its second
              or third sound it is underlined. A is one of six vowels: a, e, i, o, u.
            </blockquote>
            <p>
              Same brain. Same architecture. Same training data. Same 954 pairs. The
              only change was giving it a proper mouth.
            </p>
          </section>

          <Pulse />

          {/* The Numbers */}
          <section className="calc-section reveal">
            <h2>The Numbers</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                margin: '1.5rem 0',
                fontSize: '0.95rem',
              }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(201,168,76,0.3)' }}>
                    <th style={{ textAlign: 'left', padding: '0.75rem', color: '#c9a84c', fontFamily: 'Comfortaa, sans-serif', fontSize: '0.8rem', letterSpacing: '0.1em' }}>METRIC</th>
                    <th style={{ textAlign: 'right', padding: '0.75rem', color: '#c9a84c', fontFamily: 'Comfortaa, sans-serif', fontSize: '0.8rem', letterSpacing: '0.1em' }}>VALUE</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Parameters', '22,296,552'],
                    ['Hidden dimension', '512'],
                    ['Vocabulary (BPE)', '8,192 tokens'],
                    ['Max sequence length', '256 tokens'],
                    ['Training data', '954 pairs'],
                    ['Training time', '15 minutes'],
                    ['Training hardware', 'NVIDIA H200'],
                    ['Training cost', '$0.52'],
                    ['Phonics accuracy', '97.6%'],
                    ['Base model', 'None'],
                  ].map(([label, value], i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '0.6rem 0.75rem', color: '#999' }}>{label}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#e0e0e0', fontFamily: 'Comfortaa, sans-serif' }}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>
              For comparison: GPT-3 was trained on 300 billion tokens. We used 954
              pairs. Our model answers phonics questions with 97.6% accuracy because
              the architecture is designed for how the brain actually processes
              language, not for brute-force statistical prediction.
            </p>
          </section>

          <Pulse />

          {/* What This Is */}
          <section className="calc-section reveal">
            <h2>What This Is</h2>
            <p>
              A <strong>Neurochemical Language Model</strong> with Hodgkin-Huxley
              conductance gating on every attention head. No sigmoid. No softmax
              routing. Exponential rate functions from the 1952 Nobel Prize-winning
              equations that describe how real neurons fire.
            </p>
            <p>
              <strong>Modular architecture.</strong> Separate specialist modules for
              phonics, word types, morphology, grammar, and comprehension. Each module
              trains independently and freezes after convergence. No catastrophic
              forgetting. The substrate is stable.
            </p>
            <p>
              <strong>Neurochemical state.</strong> A 9-dimensional vector: adrenaline,
              dopamine, oxytocin, prolactin, GABA, cortisol, vasopressin, serotonin,
              acetylcholine. Each with pharmacokinetic decay at biologically accurate
              half-lives. This state modulates all processing through conductance
              gates. The chemistry shapes what the model attends to.
            </p>
            <p>
              <strong>Thalamic router.</strong> A learned routing network that directs
              input to the correct specialist module. Not Mixture of Experts with
              softmax. Complementary matching. Multiple modules can activate
              simultaneously when a question spans domains.
            </p>
          </section>

          <Pulse />

          {/* What This Is NOT */}
          <section className="calc-section reveal">
            <h2>What This Is Not</h2>
            <p>
              This is <strong>not a fine-tuned GPT</strong>. There is no base model.
              No pre-training on the internet. No transfer learning. This model learned
              to read from 954 question-answer pairs about English phonograms.
            </p>
            <p>
              This is <strong>not prompt engineering</strong>. The model generates
              answers autoregressively, token by token, from its own learned weights.
            </p>
            <p>
              This is <strong>not post-processed</strong>. Raw output. No cleanup.
              No filtering. What the model produces is what you see.
            </p>
          </section>

          <Pulse />

          {/* Honest Limitations */}
          <section className="calc-section reveal">
            <h2>Current Limitations</h2>
            <p>
              We publish our limitations because progress requires honesty about
              where you are, not just where you want to be.
            </p>
            <p>
              <strong>Exact-match dependency.</strong> The model answers trained
              questions precisely but struggles with rephrased versions. "What is a?"
              works perfectly. "What is an A?" does not. This is a data quantity
              problem, not an architecture problem.
            </p>
            <p>
              <strong>Only phonics trained.</strong> Grammar, morphology, word types,
              and comprehension modules exist in the architecture but are not yet
              trained with the BPE tokenizer.
            </p>
            <p>
              <strong>No generalization to unseen questions.</strong> The model has
              learned 954 pairs. Teaching it to generalize requires more varied
              training data and augmentation. That is next.
            </p>
          </section>

          <Pulse />

          {/* The Philosophy */}
          <section className="calc-section reveal">
            <h2>The Philosophy</h2>
            <p className="calc-lead">
              We are not trying to build a bigger model. We are trying to build
              a smarter architecture.
            </p>
            <p>
              The human brain does not process language by predicting the next token
              in a sequence of 300 billion examples. It processes language through
              specialized neural circuits modulated by neurochemistry. A child learns
              to read with a few hundred carefully structured lessons, not by ingesting
              the entire internet.
            </p>
            <p>
              Our architecture reflects this. Specialist modules. Biological gating.
              Neurochemical modulation. Structured curriculum.
            </p>
            <p>
              And it works.
            </p>
            <p style={{ fontSize: '1.3rem', color: '#c9a84c', fontFamily: 'Comfortaa, sans-serif', textAlign: 'center', margin: '2rem 0' }}>
              954 pairs. $0.52. 97.6%.
            </p>
          </section>

          <Pulse />

          {/* Try It */}
          <section className="calc-section reveal">
            <h2>Try It Yourself</h2>
            <p>
              The Reading Brain is live. Ask it a question. See the raw output.
              No post-processing. No cleanup. What the model produces is what you see.
            </p>
            <p style={{ textAlign: 'center', margin: '2rem 0' }}>
              <a
                href="/nlm-demo"
                style={{
                  display: 'inline-block',
                  background: '#c9a84c',
                  color: '#0a0a0f',
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  fontFamily: 'Comfortaa, sans-serif',
                  fontWeight: 700,
                  textDecoration: 'none',
                  fontSize: '0.95rem',
                }}
              >
                Ask the Reading Brain
              </a>
            </p>
          </section>

          <Pulse />

          {/* Footer */}
          <section className="calc-section reveal" style={{ textAlign: 'center', opacity: 0.6, fontSize: '0.85rem' }}>
            <p>Nexus Concordat Inc.</p>
            <p>Patent Pending: 63/939,190 | 63/962,385 | 63/988,485</p>
            <p style={{ marginTop: '0.5rem' }}>March 30, 2026</p>
          </section>

        </div>
      </div>
    </div>
  );
}

export default ChemistryOfProgress;
